import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import { VoyageDataSubmissionRoutes } from "../../constants/vetting/vetting.constants";
import {
    SaveVoyageDataSubmissionResponse,
    VoyageDataSubmissionModel,
    VoyageDataSubmissionClientModel,
    SubtaskCorrespondenceEmailModel,
    VoyageDataSubmissionDashboardDataModel,
    GroupedVoyageValidationErrorModel,
    CarbonAccountingEmissionsIntensityReportRequest,
    CarbonAccountingEmissionsIntensityReportResponse,
    CargoTypeDistributionResponse,
    LastFourSubmissionsSummaryResponse,
    GetVoyageDataSubmissionComputationErrorsResponse
} from "../../models/vetting/vetting.models";
import { EntityAttachmentDetailsModel } from "../../views/pages/modules/voyage-data-submission/steps/documents/voyage-data-submission-documents";

@autoinject
export class VoyageDataSubmissionService {
    constructor(private requestClient: RequestClientService) {
    }

    public saveVoyageDataSubmission(voyageDataSubmission: VoyageDataSubmissionModel,
                                    correspondenceEmails: SubtaskCorrespondenceEmailModel[], attachments: File[], cultureCode: string, customerId?: number, businessUnitId?: number): Promise<SaveVoyageDataSubmissionResponse> {
        return this.requestClient.postFormData(VoyageDataSubmissionRoutes.SaveVoyageDataSubmission, {
            correspondenceEmails: correspondenceEmails,
            voyageDataSubmission: voyageDataSubmission,
            files: attachments,
            cultureCode: cultureCode,
            customerId: customerId,
            businessUnitId: businessUnitId
        });
    }

    public getCarbonAccountingEmissionsIntensityReport(request: CarbonAccountingEmissionsIntensityReportRequest): Promise<CarbonAccountingEmissionsIntensityReportResponse> {
        return this.requestClient.get<CarbonAccountingEmissionsIntensityReportResponse>(VoyageDataSubmissionRoutes.GetCarbonAccountingEmissionsIntensityReport, request);
    }

    public dashboardData(customerId?: number): Promise<VoyageDataSubmissionDashboardDataModel> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.DashboardData}/${customerId}`, {});
    }

    public getCaDashboardSettings(customerId?: number): Promise<any> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetCaDashboardSettings}/${customerId}`, {});
    }

    public getVoyageDataSubmission(id: number): Promise<VoyageDataSubmissionClientModel> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmission}/${id}`, {});
    }

    public hasCaEnrollment(): Promise<boolean> {
        return this.requestClient.get<boolean>(`${VoyageDataSubmissionRoutes.HasCaEnrollment}`, {});
    }

    public getVoyageDataSubmissionIdIfAnyDraft(year: number, financialYearReportingPeriodId: number): Promise<number> {
        return this.requestClient.get<number>(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmissionIdIfAnyDraft}/${year}/${financialYearReportingPeriodId}`, {});
    }

    public currentCaReportingPeriod(customerId?: number): Promise<{ year: number, financialYearReportingPeriodId: number, reportingPeriodTypeId: number, financialYearTypeId: number }> {
        return this.requestClient.get<{ year: number, financialYearReportingPeriodId: number, reportingPeriodTypeId: number, financialYearTypeId: number }>(`${VoyageDataSubmissionRoutes.CurrentCaReportingPeriod}/${customerId}`, {});
    }

    public changeStatus(id: number, voyageDataSubmissionStatusId: number, comments: string, meta: string, subtaskTypes: number[],
                        statusSnippet: string, reopenStatusReasonId: number, files: File[], dataAttachments: File[], otherAttachments: File[],
                        emissionsCalculation: number, ghgIntensityCalculation: number, cultureCode: string): Promise<void> {
        return this.requestClient.postFormData(VoyageDataSubmissionRoutes.ChangeStatus, {
            id: id,
            comments: comments,
            statusId: voyageDataSubmissionStatusId,
            meta: meta,
            subtaskTypes: subtaskTypes,
            statusSnippet: statusSnippet,
            reopenedReasonId: reopenStatusReasonId,
            files: files,
            dataFiles: dataAttachments,
            otherFiles: otherAttachments,
            emissionsCalculation: emissionsCalculation,
            ghgIntensityCalculation: ghgIntensityCalculation,
            cultureCode: cultureCode
        });
    }

    public getVoyageDataSubmissionIdIfWasSubmittedOrCompleted(year: number, financialYearReportingPeriodId: number, customerId?: number): Promise<any> {
        return this.requestClient.get<any>(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmissionIdIfWasSubmittedOrCompleted}/${year}/${financialYearReportingPeriodId}/${customerId}`, {});
    }

    public getVoyageDataSubmissionErrors(voyageDataSubmissionId: number): Promise<GroupedVoyageValidationErrorModel[]> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmissionErrors}/${voyageDataSubmissionId}`, {});
    }

    public resolveValidationErrors(errorResolutions: { groupIdentifier: string, value: number, validationTypeId: number }[], customerId: number, voyageDataSubmissionId: number) {
        return this.requestClient.post(`${VoyageDataSubmissionRoutes.ResolveValidationErrors}`, {}, {
            errorResolutions: errorResolutions,
            customerId: customerId,
            voyageDataSubmissionId: voyageDataSubmissionId
        });
    }

    public getLastFourSubmissionsData(customerId: number, voyageDataSubmissionId: number): Promise<LastFourSubmissionsSummaryResponse> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetLastFourSubmissionsData}/${customerId}/${voyageDataSubmissionId}`, {});
    }

    public getCargoTypeDistribution(customerId: number, voyageDataSubmissionId: number): Promise<CargoTypeDistributionResponse> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetCargoTypeDistribution}/${customerId}/${voyageDataSubmissionId}`, {});
    }

    public getIsNotReadyForReporting(voyageId: number): Promise<boolean> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetIsNotReadyForReporting}/${voyageId}`, {});
    }

    public validateAttachments(entityId: number, files: File[], filesDetails: EntityAttachmentDetailsModel[], cultureCode?: string): Promise<[]>
    {
        return this.requestClient.postFormData(VoyageDataSubmissionRoutes.ValidateAttachments, {
            entityId: entityId,
            attachmentsDetails: filesDetails,
            files: files,
            cultureCode: cultureCode
        });
    }

    public getVoyageDataSubmissionComputationErrors(voyageDataSubmissionId: number): Promise<GetVoyageDataSubmissionComputationErrorsResponse> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmissionComputationErrors}/${voyageDataSubmissionId}`, {});
    }

    public deleteVoyageData(voyageDataSubmissionId: number): Promise<void> {
        return this.requestClient.post(`${VoyageDataSubmissionRoutes.DeleteVoyageData}`, {}, {voyageDataSubmissionId: voyageDataSubmissionId});
    }

    public getVoyageDataSubmissionTotalEmissionsAndIntensity(voyageDataSubmissionId: number): Promise<any> {
        return this.requestClient.get(`${VoyageDataSubmissionRoutes.GetVoyageDataSubmissionTotalEmissionsAndIntensity}/${voyageDataSubmissionId}`);
    }
}